import React, { useMemo, useState } from 'react'
import {
  AREA_CHART_DEFAULT_HEIGHT,
  AREA_CHART_DEFAULT_WIDTH,
  AREA_CHART_STROKE_COLOR,
  AreaChart,
  ChartFrame,
  type AreaChartPoint,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './AreaChartShowcase.css'

const SAMPLE_PRODUCTION: AreaChartPoint[] = [
  { label: 'Mon', value: 42 },
  { label: 'Tue', value: 58 },
  { label: 'Wed', value: 51 },
  { label: 'Thu', value: 73 },
  { label: 'Fri', value: 66 },
  { label: 'Sat', value: 38 },
  { label: 'Sun', value: 29 },
]

const AreaChartShowcase: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false)
  const [showXAxisLabel, setShowXAxisLabel] = useState(false)
  const [showYAxisLabel, setShowYAxisLabel] = useState(false)
  const [width, setWidth] = useState(AREA_CHART_DEFAULT_WIDTH)
  const [height, setHeight] = useState(AREA_CHART_DEFAULT_HEIGHT)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'area-chart-width',
            label: `Width (${width}px)`,
            control: (
              <input
                id="area-chart-width"
                className="ui-field-control"
                type="range"
                min={280}
                max={800}
                step={8}
                value={width}
                onChange={(event) => setWidth(Number(event.target.value))}
              />
            ),
          },
          {
            id: 'area-chart-height',
            label: `Height (${height}px)`,
            control: (
              <input
                id="area-chart-height"
                className="ui-field-control"
                type="range"
                min={120}
                max={320}
                step={8}
                value={height}
                onChange={(event) => setHeight(Number(event.target.value))}
              />
            ),
          },
          {
            id: 'area-chart-x-title',
            label: 'X axis title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="area-chart-x-title"
                  type="checkbox"
                  checked={showXAxisLabel}
                  onChange={(event) => setShowXAxisLabel(event.target.checked)}
                />
                <span>Show “Day”</span>
              </label>
            ),
          },
          {
            id: 'area-chart-y-title',
            label: 'Y axis title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="area-chart-y-title"
                  type="checkbox"
                  checked={showYAxisLabel}
                  onChange={(event) => setShowYAxisLabel(event.target.checked)}
                />
                <span>Show</span>
              </label>
            ),
          },
          {
            id: 'area-chart-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="area-chart-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, height, showXAxisLabel, showYAxisLabel, width],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Area chart"
        description="Linear area series with cyan-400 stroke and cyan-100 fill at 40% opacity — framed with sample axes and legend."
        stageClassName="ui-preview__stage--wide area-chart-showcase__stage-wrap"
      >
        <div
          className={`area-chart-showcase__sheet${
            darkMode ? ' area-chart-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Sample weekly production · {width}×{height}px
            </p>
            <div
              className="area-chart-showcase__live-frame"
              style={{ width, maxWidth: '100%' }}
            >
              <ChartFrame
                darkMode={darkMode}
                plotHeight={height}
                yTicks={['80', '60', '40', '20', '0']}
                yAxisLabel="Production ($k)"
                showYAxisLabel={showYAxisLabel}
                xLabels={SAMPLE_PRODUCTION.map((point) => point.label ?? '')}
                xAxisLabel="Day"
                showXAxisLabel={showXAxisLabel}
                legend={[
                  { label: 'Production', color: AREA_CHART_STROKE_COLOR },
                ]}
              >
                <AreaChart
                  data={SAMPLE_PRODUCTION}
                  width={width}
                  height={height}
                  maxValue={80}
                  seriesName="Production"
                  darkMode={darkMode}
                  aria-label="Sample weekly production"
                />
              </ChartFrame>
            </div>
          </section>

          <section className="area-chart-showcase__matrix">
            <h4 className="area-chart-showcase__section-title">Default</h4>
            <div
              className={`area-chart-showcase__row${
                darkMode ? ' area-chart-showcase__row--dark' : ''
              } area-chart-showcase__row--selected`}
            >
              <span className="area-chart-showcase__row-label">
                Figma default
              </span>
              <div className="area-chart-showcase__row-preview">
                <AreaChart />
              </div>
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Area chart"
        sections={propertySections}
      />
    </>
  )
}

export default AreaChartShowcase
